import { APP } from 'src/app/config/app';
import {
  FilterOptionByIdLoader,
  FilterOptionLoader,
  IFilterOption,
  IFilterOptionSource,
  ISuggestionIcon,
  ITown,
} from 'src/app/models';
import {
  CommunicationUserType,
  FilterField,
  MessageChannel,
  MessageStatus,
  SuggestionVisibility,
  UserSortType,
  UserStatus,
  UserType,
} from 'src/app/models/enums';
import { getMessageChannelIcon, getMessageDeliveryStatusIcon } from 'src/app/forms/messageForms';
import { RouteService, UsersService, VehiclesService } from 'src/app/services';
import { LiteralService } from 'src/app/services/literal/literal.service';
import { ServiceInfoService } from 'src/app/services/service-info/service-info.service';
import {
  suggestionPriorityIcon,
  suggestionStatusIcon,
  suggestionVisibilityIcon,
} from 'src/app/utils/suggestionTickets';
import { FilterOptionList } from './filterOptionList';

export function checkboxFieldValues(field: FilterField, userType?: CommunicationUserType): readonly string[] {
  if (field === FilterField.SUGGESTION_TYPES && userType) return APP.SUGGESTION_TYPE_OPTIONS[userType];
  return APP.FILTER_CHECKBOX_OPTIONS[field] ?? [];
}

export function checkboxOptionLabel(literalService: LiteralService, field: FilterField, value: string): string {
  const definition = APP.FILTER_FIELDS[field];
  const key = definition.rawOptionLabels ? value : value.toLowerCase();
  return literalService.get(`${definition.optionLabelPrefix ?? ''}${key}`, true);
}

export function checkboxOptionIcon(field: FilterField, value: string): ISuggestionIcon | null {
  switch (field) {
    case FilterField.SUGGESTION_PRIORITIES:
      return suggestionPriorityIcon(value);
    case FilterField.SUGGESTION_STATUS:
      return suggestionStatusIcon(value);
    case FilterField.SUGGESTION_VISIBILITIES:
      return { src: suggestionVisibilityIcon(value as SuggestionVisibility), filterClass: 'filter-primary-purple' };
    case FilterField.MESSAGE_DELIVERY_STATUS:
      return getMessageDeliveryStatusIcon(value as MessageStatus);
    case FilterField.MESSAGE_CHANNELS:
      return { src: getMessageChannelIcon(value as MessageChannel), filterClass: '' };
    default:
      return null;
  }
}

export function checkboxOptionTooltip(literalService: LiteralService, field: FilterField, value: string): string {
  const prefix = APP.FILTER_FIELDS[field].optionTooltipPrefix;
  return prefix ? literalService.get(`${prefix}${value}`, true) : '';
}

export function checkboxFieldSource(
  literalService: LiteralService,
  field: FilterField,
  userType: () => CommunicationUserType | undefined = () => undefined,
): IFilterOptionSource {
  return {
    loader: () => Promise.resolve({ content: [...checkboxFieldValues(field, userType())], last: true }),
    toOption: (value: string) => ({
      id: value,
      value,
      label: checkboxOptionLabel(literalService, field, value),
      icon: checkboxOptionIcon(field, value) ?? undefined,
      tooltip: checkboxOptionTooltip(literalService, field, value) || undefined,
    }),
  };
}

export function serviceOption(service: any): IFilterOption {
  return {
    id: service.id,
    value: { id: service.id, tag: service.tag, name: service.name },
    label: service.name ? `${service.tag}: ${service.name}` : service.tag,
  };
}

export function routeOption(route: any): IFilterOption {
  return { id: route.id, value: { id: route.id, name: route.name }, label: route.name };
}

export function vehicleOption(vehicle: any): IFilterOption {
  return { id: vehicle.id, value: { id: vehicle.id, name: vehicle.name }, label: vehicle.name };
}

export function driverOption(driver: any): IFilterOption {
  const name = `${driver.firstName ?? ''} ${driver.lastName ?? ''}`.trim();
  return { id: driver.id, value: { id: driver.id, name }, label: name };
}

export function serviceByIdLoader(serviceInfoService: ServiceInfoService): FilterOptionByIdLoader {
  return (id) => serviceInfoService.getServiceInfo(id);
}

export function routeByIdLoader(routeService: RouteService): FilterOptionByIdLoader {
  return (id) => routeService.getService(id);
}

export function vehicleByIdLoader(vehiclesService: VehiclesService): FilterOptionByIdLoader {
  return (id) => vehiclesService.getVehicle(id);
}

export function driverByIdLoader(usersService: UsersService): FilterOptionByIdLoader {
  return (id) => usersService.getUser(UserType.Driver, id);
}

export function serviceLoader(serviceInfoService: ServiceInfoService, towns: () => ITown[]): FilterOptionLoader {
  return (page, search) =>
    serviceInfoService.getServiceInfosSearch(FilterOptionList.pageSize(), page, towns(), search) as any;
}

export function routeLoader(routeService: RouteService, serviceIds: () => number[]): FilterOptionLoader {
  return (page, search) => {
    const ids = serviceIds();
    if (!ids.length) return Promise.resolve({ content: [], last: true });
    return routeService.getRoutesSearch(ids, page, FilterOptionList.pageSize(), search) as any;
  };
}

export function vehicleLoader(vehiclesService: VehiclesService, clientIds: () => number[]): FilterOptionLoader {
  return (page, search) =>
    vehiclesService.filterVehicles(page, FilterOptionList.pageSize(), clientIds(), [], undefined, search);
}

export function driverLoader(usersService: UsersService, clientIds: () => number[]): FilterOptionLoader {
  return (page, search) =>
    usersService.filterDrivers(
      UserSortType.ALPHABETICALLY,
      page,
      FilterOptionList.pageSize(),
      clientIds(),
      [],
      [UserStatus.ACTIVE],
      search,
    ) as any;
}
